/**
 * ProjectCustomTable
 * ---------------------------------------------------------
 * A reusable table component (react-table based) that is mainly
 * used for Project-related lists when `tableType === "API"`.
 *
 * Features:
 * - Column ordering and persistence via localStorage
 * - Server-like pagination with react-table (usePagination)
 * - Infinite scroll rendering for smoother UX
 * - Row selection (checkboxes) with optional `selectedRowsData`
 * - Custom cell click navigation for "Project Resources"
 * - Themed styles based on `theme` (light/dark)
 * - Optional column manager modal to show/hide/reorder columns
 */

import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import commonStyle from "../../Screens/common.module.css";
import { useTable, useSortBy, useFilters, useColumnOrder, usePagination } from "react-table";
import { getFilterObject } from "../../Values/utils";
import { connect, useDispatch } from "react-redux";
import TableHeader from "../TableHeader/TableHeader";
import ColumnManagerModal from "../Modals/ColumnManagerModal/ColumnManager";
import { setClearFilter, showSnackbarAction } from "../../Redux/actions";
import { useHistory } from "react-router";
import { FiEdit3 } from "react-icons/fi";
import Loading from "../Loading/Loading";
import InfiniteScroll from "react-infinite-scroll-component";
import { useLocation } from "react-router-dom";
import DistributionModel from "../Modals/DistributionModel/DistributionModel";
import { themeColors } from "../../Components/theme";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

type props = {

  /** Column definitions for react-table */
  tableColumns: any[];

  /** Raw row data array */
  tableRows: any[] | undefined;

  /** Selected row indices (page-based index pattern) */
  selectedRows: any[];
  setSelectedRows: (data: any[]) => void;

  /** Controls behavior and enable/disable certain features */
  tableType:
  | "Organization"
  | "Super admin"
  | "User"
  | "Task"
  | "CallLogs"
  | "API"
  | "NEWS"
  | "LeadDistributor"
  | "DrillDown"
  | "Custom Button"
  ;

  /** Column manager modal visibility */
  showColumnModal: boolean;
  hideColumnModal: () => void;

  /** Holds full data of selected rows (optional) */
  selectedRowsData?: any[];
  setSelectedRowsData?: (data: any[]) => void;

  /** Search binding from parent (optional) */
  setSearchedItem?: (text: any) => void;

  /** Organization + users info for context */
  organization: any;
  organizationUsers: any[];
  role: any;

  /** For Organization branch/uid logic (not used here directly for API type) */
  setBranch?: (text: boolean) => void;
  setUid?: (data: string) => void;

  /** Current user info */
  user: any;

  /** API related handlers (used when tableType === "API") */
  setApiModal?: (data: boolean) => void;
  setApiEdit?: (data: boolean) => void;
  setApiData?: (data: any) => void;

  /** Pagination info for external parent usage */
  setPageNumber?: (data: any) => void;
  setPageCount?: (data: any) => void;

  /** Theme flag (true = light, false = dark) */
  theme: any;

  /** Any custom toggle helper (not used in this file) */
  setToggleCondition?: any;

  /** Trigger to clear filters from parent */
  clearFilterTable: boolean;
};

const ProjectCustomTable: FunctionComponent<props> = ({
  tableColumns,
  tableRows,
  selectedRows,
  setSelectedRows,
  tableType,
  showColumnModal,
  hideColumnModal,
  selectedRowsData,
  setSelectedRowsData,
  setSearchedItem,
  organization,
  organizationUsers,
  role,
  setBranch,
  setUid,
  user,
  setApiModal,
  setApiEdit,
  setApiData,
  setPageNumber,
  setPageCount,
  theme,
  setToggleCondition,
  clearFilterTable
}) => {
  const dispatcher = useDispatch();
  const history = useHistory();

  /** "Select all" checkbox state */
  const [checked, setChecked] = useState(false);

  /** Lead distributor modal control (kept for parity with other tables) */
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [load, setLoad] = useState(false);

  /** Data and index passed to DistributionModel */
  const [distributionData, setDistributionData] = useState();
  const [indexs, setIndex] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(20);

  const [recordCounts, setRecordsCount] = useState<any[]>([]);
  const [CurrLeader, setCurrLeader] = useState(-1);
  const [active, setActiveUsers] = useState(0);

  const isMounted = React.useRef(false);

  /**
 * When parent triggers `clearFilterTable` change,
 * reset filters, localStorage keys, selection, and possibly route.
 */
  useEffect(() => {
    if (isMounted.current) {
      clearFilter();
    } else {
      isMounted.current = true;
    }
  }, [clearFilterTable]);


  /**
 * Select/unselect all rows on the current page.
 *
 * Behavior is special when tableType === "API":
 * - Uses page indices & row.original objects
 */
  const selectAll = (checked: boolean) => {
    if (
      tableType === "API"
    ) {

      if (checked === true) {
        if (page) {

          let selectRowsIndex: any[] = [...selectedRows];

          Array.from(Array(page?.length).keys()).forEach(
            (index) => {
              selectRowsIndex.push(
                pageSize * pageIndex + index + 1
              );
            }
          );
          setSelectedRows(selectRowsIndex);
        }

        if (page) {
          if (selectedRowsData) {
            let selectRows: any[] = [...selectedRowsData];
            page.forEach((item: any) => {
              selectRows.push(item.original);
            });
            setSelectedRowsData &&
              setSelectedRowsData(selectRows);
          } else {
            let selectRows: any[] = [];
            page.forEach((item: any) => {
              selectRows.push(item.original);
            });
            setSelectedRowsData &&
              setSelectedRowsData(selectRows);
          }
        }
      } else {
        setSelectedRows([]);
        setSelectedRowsData && setSelectedRowsData([]);
      }
    } else {
      if (checked === true) {
        setSelectedRows(
          Array.from(Array(page?.length).keys())
        );
      } else {
        setSelectedRows([]);
      }
    }
  };

  /**
 * Memoized react-table columns & data
 */
  const columns = useMemo(() => tableColumns, [tableColumns]);
  const data = useMemo(() => (tableRows ? tableRows : []), [tableRows]);

  /**
    * Initialize react-table with filters, sort, column order, and pagination.
    */
  const tableInstance: any = useTable(
    {
      columns,
      data,
    },
    useFilters,
    useSortBy,
    useColumnOrder,
    usePagination
  );

  const {
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state,
    gotoPage,
    pageCount,
    setPageSize,
    allColumns,
    //@ts-ignore
    setColumnOrder,
    //@ts-ignore
    toggleHideAllColumns,
  } = tableInstance;

  const { pageIndex, pageSize } = state;

  /**
  * On mount:
  * - Set default page size to 100
  * - Restore column order from localStorage
  */
  useEffect(() => {
    setPageSize(100);
    const savedColumns = localStorage.getItem("columns");
    if (savedColumns) {
      setColumnOrder(JSON.parse(savedColumns));
    }
    // eslint-disable-next-line
  }, []);

  /**
   * Maintain count of active users in the organization
   * (even though not heavily used here, kept consistent with other tables).
   */
  useEffect(() => {
    if (organizationUsers) {
      let activeUsers = organizationUsers.filter(
        (item) => item.status === "ACTIVE"
      );
      setActiveUsers(activeUsers.length);
    }
  }, [organizationUsers]);

  /**
  * Build page size options from static list + total rows length.
  */
  useEffect(() => {
    let l = [10, 25, 50, 100, 200, 500];
    l.push(rows.length);
    setRecordsCount(l);
  }, [rows]);

  /**
  * Update column order and persist to localStorage.
  */
  const setColumns = (columnList: any) => {
    setColumnOrder(columnList);
    localStorage.setItem(
      "columns",
      JSON.stringify(columnList)
    );
  };

  /**
  * Build filter options from tableRows and dispatch to Redux
  * (used globally for filtering UI).
  */
  useEffect(() => {
    if (tableRows) {
      getFilterObject(tableRows, dispatcher);
    }
  }, [tableRows, dispatcher]);

  /**
 * Single row checkbox handler:
 * - Maintains both selectedRows (indices) and selectedRowsData (full row objects)
 *   when tableType === "API"
 * - Always updates selectedRows by index
 */
  const onCheck = (event: any, row: any, index: number) => {
    if (
      tableType === "API"
    ) {
      if (event.target.checked) {
        let data = [...selectedRows];

        data.push(index);
        setSelectedRows(data);
        if (selectedRowsData) {
          let rowData = [...selectedRowsData];
          rowData.push(row);
          setSelectedRowsData &&
            setSelectedRowsData(rowData);
        }
      } else if (!event.target.checked) {
        let data = [...selectedRows];
        let item = selectedRows.indexOf(index);
        if (item > -1) {
          data.splice(item, 1);
        }
        setSelectedRows(data);
        if (selectedRowsData) {
          let rowData = [...selectedRowsData];
          let val = selectedRowsData.indexOf(row);
          if (val > -1) {
            rowData.splice(val, 1);
          }
          setSelectedRowsData &&
            setSelectedRowsData(rowData);
        }
      }
    }

    if (event.target.checked) {
      let data = [...selectedRows];
      data.push(index);
      setSelectedRows(data);
    } else if (!event.target.checked) {
      let data = [...selectedRows];
      let item = selectedRows.indexOf(index);
      if (item > -1) {
        data.splice(item, 1);
      }
      setSelectedRows(data);
    }
  };

  /**
 * Reset local filters, search term, and localStorage keys.
 * Also navigates to `/projects` when tableType === "API".
 */
  const clearFilter = () => {
    setSearchedItem && setSearchedItem("");
    var key: any;
    for (var i = 0; i < localStorage.length; i++) {
      key = localStorage.key(i);

      if (key !== "columns" && key !== 'isLightMode' && key !== 'lastAlertTime') {
        localStorage.removeItem(key);
      }
    }
    dispatcher(setClearFilter(true));
    if (tableType === 'API') {
      history.push('/projects');
    }
  };

  /**
  * Theme-aware styles (background, text, borders, button colors)
  */
  const tdStyles = {
    backgroundColor: theme ? themeColors.tdBackgroundColor : themeColors.tdBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const footerStyles = {
    backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }

  const buttonStyles = {
    backgroundColor: theme ? themeColors.buttonPrimary : themeColors.buttonPrimaryDark,
    color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
  }
  const trBorderStyles = {
    borderBottom: theme ? `1px solid ${themeColors.trBorderColor}` : `1px solid ${themeColors.trBorderColorDark}`,
  }


  return (
    <>
      {/* Main scrollable wrapper for the table */}
      <div className={commonStyle.child} id="scrollable">

        {/* Global loading overlay */}
        {load === true && <Loading />}
        <div style={{ width: "max-content" }}>

          {/* Table header row (select all + column headers) */}
          <TableHeader
            headerGroups={headerGroups}
            selectAll={(data) => {
              selectAll(data);
              setChecked(data);
            }}
            panel={tableType}
            allCheck={checked}
          />

          {/* InfiniteScroll to lazily render rows while maintaining react-table pagination */}
          <InfiniteScroll
            style={{ overflow: "visible", minHeight: "100vh", width: "100%", marginBottom: "-10rem", ...tdStyles }}
            dataLength={currentLimit}
            next={() => setCurrentLimit(currentLimit + 20)}
            hasMore={rows.length > currentLimit}
            loader={""}
            scrollableTarget="scrollable"
            className={commonStyle.infiniteScroll}
          >
            {tableRows && (
              <div {...getTableBodyProps()}>
                {page
                  .slice(0, currentLimit)
                  .map((row: any, index: number) => {
                    prepareRow(row);
                    return (
                      <div
                        {...row.getRowProps()}
                        style={
                          row.original.status === "INACTIVE"
                            ? {
                              display: "flex",
                              flexDirection: "row",
                              ...trBorderStyles
                            }
                            : {
                              display: "flex",
                              flexDirection: "row",
                              ...trBorderStyles
                            }
                        }
                      >
                        {/* Sticky sidebar cell: checkbox + edit icon + row index */}
                        <div className={commonStyle.sticky} style={tdStyles}>
                          <input
                            style={{ marginRight: "10px", accentColor: "#272944" }}
                            type="checkbox"
                            onChange={(e) => {
                              if (
                                row.original
                                  .transfer_status === true
                              ) {
                                dispatcher(
                                  showSnackbarAction(
                                    "This Lead Cannot be Transfered",
                                    "error"
                                  )
                                );
                              } else {
                                onCheck(
                                  e,
                                  row.original,
                                  pageSize * pageIndex +
                                  index +
                                  1
                                );
                              }
                            }}
                            // Project rows with status "Launched" cannot be selected
                            disabled={row.original.project_status === "Launched" ? true : false}
                            checked={selectedRows.includes(
                              pageSize * pageIndex +
                              index +
                              1
                            )}
                          />

                          {/* Edit icon for non-Task and non-Team users */}
                          {tableType !== "Task" && user.user_first_name !== "Team" && (
                            <FiEdit3
                              size={15}
                              color="#272944"
                              style={{ marginRight: '7px' }}
                              onClick={() => {
                                if (tableType === "API") {
                                  if (row.original.transfer_status === true) {
                                    dispatcher(
                                      showSnackbarAction(
                                        'Lead With Transfer Status TRUE Cannot be Edited!!',
                                        'error'
                                      )
                                    );
                                  } else {
                                    let data = { ...row.original };
                                    history.push({
                                      pathname: '/editProjects',
                                      state: {
                                        detail: data,
                                      },
                                    });
                                  }
                                }
                              }}
                            />
                          )}

                          {/* Distribution modal (not actively triggered here, but kept for parity) */}
                          {showDistributionModal && (
                            <DistributionModel
                              open={showDistributionModal}
                              organization_id={
                                user.organization_id
                              }
                              close={() => {
                                setShowDistributionModal(
                                  false
                                );
                              }}
                              data={distributionData}
                              index={indexs}
                            />
                          )}
                          {/* Render row index (1-based) */}
                          {pageSize * pageIndex + index + 1}
                        </div>

                        {/*  render table data all at a time */}
                        {row.cells.map((cell: any) => {
                          return (
                            <div
                              className={commonStyle.td}
                              {...cell.getCellProps()}
                              style={
                                tableType === "LeadDistributor"
                                  ? { minWidth: "210px", maxWidth: "210px", ...tdStyles }
                                  : { ...tdStyles }
                              }
                              onClick={() => {
                                // Special click behavior for "Project Resources" column in API table:
                                // Navigate to /projectDetails with selected project data.

                                if (
                                  cell.column.Header === 'Project Resources' &&
                                  tableType === 'API'
                                ) {
                                  if (cell.row.original.transfer_status === true) {
                                    dispatcher(
                                      showSnackbarAction(
                                        'This is a Transferred Lead!!',
                                        'error'
                                      )
                                    );
                                  } else {
                                    history.push({
                                      pathname: "/projectDetails",
                                      state: {
                                        AllProjectdata: tableRows,
                                        detail: {
                                          ...cell.row.original,
                                          created_at: cell.row.original["created_at"]
                                            ? cell.row.original["created_at"].toDate()
                                              ? cell.row.original["created_at"].toDate()
                                              : ""
                                            : "",
                                        },
                                      },
                                    });
                                  }
                                }
                              }}
                            >
                              {cell.column.Header === 'Project Resources' &&
                                tableType === 'API' ? (
                                <button type="button" style={{ ...footerStyles, height: 30, width: 70, borderRadius: 10 }}>Upload</button>
                              ) : (
                                cell.render("Cell")
                              )}
                            </div>
                          );
                        })}

                      </div>
                    );
                  })}
              </div>
            )}
          </InfiniteScroll>
        </div>
      </div>

      {/* Footer: pagination + counts */}
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: "center", borderRadius: "0.5rem", padding: "0.5rem", marginTop: "1rem", marginBottom: "-1rem", ...footerStyles }}>
        <div className={commonStyle.footer_table} style={{ height: "35px" }}>
          {/* Selected row count */}
          <p className={commonStyle.countHead} style={{ fontSize: "12px", paddingTop: "7px" }} >
            Selected Records: {selectedRows.length}
          </p>
          {/* Pagination controls */}
          <div style={{
            display: "flex",
            alignItems: "center",
            margin: "0 0 0 0"
          }}>
            <span>
              Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
            </span>
            <span>
              | Go to Page:{" "}
              <input
                type="number"
                className={commonStyle.nSelecct}
                min={1}
                max={pageOptions.length}
                defaultValue={pageIndex + 1}
                onChange={(e) => {
                  let pageNumber = e.target.value ? Number(e.target.value) : 1;
                  if (pageNumber < 1) pageNumber = 1;
                  if (pageNumber > pageOptions.length) pageNumber = pageOptions.length;
                  gotoPage(pageNumber - 1);
                  setPageNumber && setPageNumber(pageNumber);
                }}
                style={{
                  width: "55px",
                  height: "24px",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  background: "#fff",
                  color: "#222",
                  fontSize: "12px",
                  textAlign: "center",
                  margin: "0 4px"
                }}
              />
            </span>

            {/* Page size dropdown */}
            <select
              value={pageSize}
              className={commonStyle.dropDownSelecct}
              style={{
                fontSize: "12px",
                fontWeight: 400,
                height: "24px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                background: "#fff",
                color: "#222",
                margin: "0 4px"
              }}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageCount && setPageCount(Number(e.target.value));
              }}
            >
              {recordCounts.map((pagesize) => (
                <option key={pagesize} value={pagesize}>
                  Show {pagesize}
                </option>
              ))}
            </select>

            {/* First page */}
            <button
              className={commonStyle.addBox}
              onClick={() => {
                gotoPage(0);
                setChecked(false);
                setPageNumber && setPageNumber(1);
              }}
              disabled={!canPreviousPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !canPreviousPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: canPreviousPage ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              {"<<"}
            </button>
            <button
              className={commonStyle.addBox}
              onClick={() => {
                previousPage();
                setChecked(false);
                setPageNumber && setPageNumber(pageIndex);
              }}
              disabled={!canPreviousPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !canPreviousPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: canPreviousPage ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              <ChevronLeftIcon style={{ fontSize: "16px" }} />
            </button>

            {/* Previous page */}
            <button
              className={commonStyle.addBox}
              onClick={() => {
                nextPage();
                setChecked(false);
                setPageNumber && setPageNumber(pageIndex + 2);
              }}
              disabled={!canNextPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !canNextPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: canNextPage ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              <ChevronRightIcon style={{ fontSize: "16px" }} />
            </button>

            {/* Next page */}
            <button
              className={commonStyle.addBox}
              onClick={() => {
                gotoPage(pageCount - 1);
                setChecked(false);
                setPageNumber && setPageNumber(pageCount);
              }}
              disabled={!canNextPage}
              style={{
                width: "32px",
                height: "24px",
                minWidth: "32px",
                padding: 0,
                backgroundColor: !canNextPage ? "#e5e7eb" : buttonStyles.backgroundColor,
                color: buttonStyles.color,
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                cursor: canNextPage ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 2px"
              }}
            >
              {">>"}
            </button>
          </div>

          {/* Total records info */}
          <p style={{ fontSize: "12px", paddingTop: "7px" }} className={commonStyle.countHead}>
            Total Records: {rows.length ? rows.length : 0}
          </p>
        </div>
      </div>

      {/* Column manager modal to reorder/show/hide columns */}
      {showColumnModal === true && (
        <ColumnManagerModal
          columnsList={allColumns}
          open={showColumnModal}
          close={hideColumnModal}
          onChangeColumns={(data: any[]) =>
            setColumns(data)
          }
          toggleAllColumns={toggleHideAllColumns}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    organizationUsers: state.organizationUsers.data,
    role: state.user.role,
    organization: state.organization,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(ProjectCustomTable);
